import { useState, useEffect, useRef } from "react";
import { Calendar, Camera, Play, FileText, User, X, Clock, ChevronRight, Download, Eye } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";
import newsGovernmentReforms from "@/assets/news-government-reforms.jpg";
import newsSectorGrowth from "@/assets/news-sector-growth.jpg";
import newsConference2025 from "@/assets/news-conference-2025.jpg";
import newsEmergencyFund from "@/assets/news-emergency-fund.jpg";
import anniversaryImage from "@/assets/natcon-10th-anniversary.jpg";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, TextPlugin);

const NewsMedia = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  
  const headerRef = useRef<HTMLDivElement>(null);
  const featuredRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const videoModalRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const filters = ["All", "Latest News", "Events", "Blog & Articles", "Photo Galleries", "Video Galleries"];

  // Video gallery items
  const videoItems = [
    {
      id: 101,
      type: "Video Galleries",
      date: "December 2025",
      title: "NATCON SACCO 10th Anniversary Celebration",
      description: "Watch the highlights from our 10th anniversary celebration - a decade of growth, resilience, and shared success with our members.",
      videoUrl: "https://tafcoirhmailxaquraqm.supabase.co/storage/v1/object/public/ANIVERSARY%20VIDEO/VID%201.mp4",
      duration: "Full Video"
    }
  ];

  const openVideoModal = (video: any) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
    document.body.style.overflow = 'unset';
  };

  const newsItems = [
    {
      id: 1,
      type: "Events",
      date: "December 5, 2025",
      title: "NATCON SACCO 10th Anniversary Celebration",
      author: "By Natcon Sacco",
      category: "Milestone Event",
      excerpt: "Join us in celebrating a decade of growth, resilience, and shared success. From Kshs. 200,000 to over Kshs. 100 million in member savings!",
      content: "We are delighted to invite you to celebrate a significant milestone for our Sacco - NATCON SACCO 10th Anniversary! Join us as we reflect on our journey and envision the future of our SACCO.\n\n**A Decade of Growth**\n\nEstablished in 2014, we are proud to announce that our SACCO has grown from just Kshs. 200,000 in its inception to over Kshs. 100 million in member savings. This achievement is a testament to the hard work and dedication of our members and reflects the vibrancy of the Sacco.\n\n**Event Details**\n\n📅 **Date:** Friday, 5 December 2025\n⏰ **Time:** 8:00 AM to 1:30 PM\n📍 **Venue:** Radisson Blu Hotel\n👔 **Dress Code:** Smart Casual\n🎯 **Theme:** Empowering Members, Building the Nation: A Decade of Cooperative Growth\n\n**What to Expect**\n\nJoin us for a morning of celebration that includes:\n- Reflection on our 10-year journey and achievements\n- Recognition of founding members and key contributors\n- Vision presentation for the next decade\n- Networking opportunities with fellow members\n- Refreshments and entertainment\n\n**RSVP**\n\nPlease confirm your attendance by calling +254 111 485 951. We look forward to celebrating this momentous occasion with you!\n\n**Our Journey**\n\nFrom humble beginnings with just Kshs. 200,000, NATCON SACCO has grown to become a trusted financial partner for our members. This growth represents not just numbers, but the dreams we've helped realize, the homes we've helped build, and the futures we've helped secure.\n\nThank you for being part of our story. Let's celebrate together!",
      image: anniversaryImage,
      featured: true,
      readTime: "3 min read",
      location: "Radisson Blu Hotel",
      time: "8:00 AM - 1:30 PM",
      theme: "Empowering Members, Building the Nation: A Decade of Cooperative Growth",
      dressCode: "Smart Casual"
    },
    {
      id: 2,
      type: "Blog & Articles",
      date: "August 12, 2025",
      title: "Government Reforms Set to Strengthen SACCO Sector Accountability and Growth",
      author: "By Natcon Sacco",
      category: "Policy & Regulation",
      excerpt: "The Kenyan government has embarked on a critical review of the SACCO Societies Act 2008, signaling a new era of accountability, transparency, and robust governance in the SACCO sector.",
      content: "The Kenyan government has embarked on a critical review of the SACCO Societies Act 2008, signaling a new era of accountability, transparency, and robust governance in the SACCO sector. This reform initiative is led by a committee of both local and international experts, whose mandate is to address longstanding challenges such as corruption and mismanagement that have, at times, eroded public confidence in SACCOs.\n\n**Key Reform Areas**\n\nPrime Cabinet Secretary Musalia Mudavadi highlighted that the review will result in key amendments to the existing legislation and introduce fresh regulations aimed at enhancing the operational efficiency of SACCOs. These policy changes align with broader efforts to empower rural communities by strengthening cooperatives in vital income-generating sectors such as coffee, cotton, dairy, beef, and leather.\n\n**Addressing Financial Malpractices**\n\nIn parallel, the government is intensifying its crackdown on financial malpractices, with efforts to recover misappropriated funds and ensure that responsible officials face legal consequences. These comprehensive reforms underscore an unwavering commitment to restoring the integrity and trustworthiness of the cooperative movement, which plays a strategic role in Kenya's economic and financial inclusion agenda.\n\n**Impact on Cooperative Societies**\n\nFor Natcon SACCO and other cooperative societies, these reforms promise a more secure and flourishing environment to serve members better while contributing meaningfully to local and national development. The strengthened regulatory framework will provide greater confidence to both existing and potential members, fostering continued growth in the sector.\n\n**Looking Forward**\n\nAs these reforms take shape, SACCOs across Kenya are preparing to adapt to the new regulatory landscape. This presents an opportunity for enhanced transparency, improved governance structures, and more robust financial management practices that will benefit all stakeholders in the cooperative movement.",
      image: newsGovernmentReforms,
      featured: false,
      readTime: "5 min read"
    },
    {
      id: 3,
      type: "Blog & Articles", 
      date: "August 10, 2025",
      title: "SACCO Sector Growth Hits New Highs with Increased Member Returns and Asset Growth", 
      author: "By Natcon Sacco",
      category: "Industry Insights",
      excerpt: "Kenya's SACCO sector is experiencing remarkable growth, demonstrating its vital role as a pillar of the country's financial system with 10% increase in total assets.",
      content: "Kenya's SACCO sector is experiencing remarkable growth, demonstrating its vital role as a pillar of the country's financial system. In 2024, the sector recorded an impressive 10% increase in total assets, reaching a staggering Sh1.8 trillion. This robust growth translates directly into tangible benefits for SACCO members, who enjoy average returns of about 10% in dividends and interest on their savings.\n\n**Record-Breaking Performance**\n\nWith a loan portfolio exceeding Sh1.1 trillion and member savings totaling over Sh1.2 trillion, SACCOs are mobilizing a significant proportion of national savings, accounting for roughly 30% of Kenya's total savings. This underlines the importance of SACCOs in fostering financial inclusion, especially for segments often underserved by traditional banking institutions.\n\n**Technology and Innovation**\n\nTechnology adoption is a top priority for the sector as it seeks to improve competitiveness and member experience. Plans are underway to develop a shared technology platform and establish a central liquidity facility to facilitate better cooperation among SACCOs. These innovations are complemented by ongoing capacity-building programs designed to equip SACCO leaders with the skills needed to manage increasingly large and complex cooperative societies.\n\n**Member Benefits**\n\nThe sector's growth has translated into tangible benefits for members, including:\n- Higher dividend payments averaging 10% annually\n- Improved loan accessibility with competitive interest rates\n- Enhanced digital banking services\n- Expanded product offerings including insurance and investment options\n\n**Future Outlook**\n\nNatcon SACCO's members stand to benefit greatly from these positive sector trends, positioning the SACCO as a reliable and rewarding financial partner. As the sector continues to evolve, members can expect even more innovative financial solutions and improved service delivery.",
      image: newsSectorGrowth,
      featured: false,
      readTime: "4 min read"
    },
    {
      id: 4,
      type: "Blog & Articles",
      date: "August 8, 2025", 
      title: "Upcoming SACCO Sector Conference 2025 to Focus on Challenges and Opportunities",
      author: "By Natcon Sacco",
      category: "Events & Conferences",
      excerpt: "From August 20 to 22, 2025, the vibrant city of Kisumu will host the much-anticipated SACCO Sector Conference 2025.",
      content: "From August 20 to 22, 2025, the vibrant city of Kisumu will host the much-anticipated SACCO Sector Conference 2025. This significant event will convene SACCO leaders, policymakers, financial experts, and stakeholders from across Kenya to engage in meaningful discourse about the sector's future.\n\n**Conference Agenda Highlights**\n\nAmong the main agenda items are regulatory reforms designed to strengthen the sector's framework, technological advancements to enhance service delivery, and strategies focused on improving member engagement and financial inclusivity. Participants will also explore innovative approaches to sustain growth amid evolving market and economic conditions.\n\n**Key Topics to be Addressed:**\n- Digital transformation in the SACCO sector\n- Regulatory compliance and governance best practices\n- Financial inclusion strategies for underserved communities\n- Risk management and cybersecurity in digital banking\n- Sustainable business models for long-term growth\n\n**Networking and Collaboration**\n\nThe conference serves as a unique platform for SACCOs to network, share best practices, and align their strategies with Kenya's national economic priorities. For Natcon SACCO, participation in or alignment with the conference outcomes can open doors to new opportunities, partnerships, and insights critical for sustained success.\n\n**Expected Outcomes**\n\nAs the SACCO landscape evolves, conferences like this play a crucial role in shaping the trajectory of cooperative societies and ensuring they continue to deliver value to their members. The event is expected to produce actionable recommendations for sector improvement and establish collaborative frameworks for future development.\n\n**Registration and Participation**\n\nSACCO leaders, board members, and senior management teams are encouraged to attend this transformative event. The conference will feature expert speakers, panel discussions, and interactive workshops designed to equip participants with the knowledge and tools needed for success in the evolving financial landscape.",
      image: newsConference2025,
      featured: false,
      readTime: "3 min read"
    },
    {
      id: 5,
      type: "Blog & Articles",
      date: "August 5, 2025",
      title: "5 Simple Steps to Start Building Your Emergency Fund Today",
      author: "By Natcon Sacco",
      category: "Saving Tips",
      excerpt: "Financial security begins with preparation. Learn how to establish an emergency fund that protects you and your family from unexpected expenses and economic uncertainties.",
      content: "Financial security begins with preparation. An emergency fund is your financial safety net, protecting you and your family from unexpected expenses and economic uncertainties. Here's how to start building yours today.\n\n**Step 1: Set a Clear Goal**\n\nFinancial experts recommend saving 3-6 months of living expenses. Start by calculating your monthly essential costs including rent, utilities, food, and transportation. This becomes your target amount.\n\n**Step 2: Start Small and Stay Consistent**\n\nDon't let a large goal intimidate you. Begin with whatever amount you can afford, even if it's just KES 500 per week. The key is consistency. Natcon SACCO's automatic savings plans make this effortless by transferring money from your salary before you even see it.\n\n**Step 3: Create a Separate Account**\n\nKeep your emergency fund separate from your regular checking account to avoid the temptation to spend it. A dedicated savings account at Natcon SACCO offers both separation and competitive interest rates, helping your money grow while it waits.\n\n**Step 4: Cut Unnecessary Expenses**\n\nReview your monthly spending and identify areas where you can cut back. That daily coffee, unused subscriptions, or impulse purchases can be redirected to your emergency fund. Small sacrifices today create significant security tomorrow.\n\n**Step 5: Boost Your Fund with Windfalls**\n\nWhenever you receive unexpected money—tax refunds, bonuses, gifts, or dividends from your SACCO shares—deposit a portion directly into your emergency fund. This accelerates your progress without impacting your regular budget.\n\n**The Natcon SACCO Advantage**\n\nAs a Natcon SACCO member, you have access to specialized savings products designed for emergency funds. Our members earn competitive dividends while maintaining easy access to their money when emergencies arise. Plus, your emergency savings can serve as collateral for emergency loans if needed.\n\n**Common Mistakes to Avoid**\n\n- Using your emergency fund for non-emergencies\n- Stopping contributions once you reach your goal (inflation increases living costs)\n- Keeping emergency funds in non-liquid investments\n- Not replenishing the fund after using it\n\n**When to Use Your Emergency Fund**\n\nTrue emergencies include:\n- Unexpected medical expenses\n- Job loss or income reduction\n- Urgent home or car repairs\n- Family emergencies\n\nNon-emergencies include vacations, holiday shopping, or planned purchases—save separately for these.\n\n**Start Today**\n\nThe best time to start building your emergency fund was yesterday. The second-best time is today. Visit any Natcon SACCO branch or use our mobile banking app to open a dedicated emergency savings account. Your future self will thank you.",
      image: newsEmergencyFund,
      featured: false,
      readTime: "4 min read"
    }
  ];

  const filteredItems = activeFilter === "All" 
    ? newsItems 
    : newsItems.filter(item => item.type === activeFilter);

  const featuredItems = newsItems.filter(item => item.featured);
  const regularItems = newsItems.filter(item => !item.featured);

  const getCategoryIcon = (type: string) => {
    switch (type) {
      case "Latest News":
        return <FileText className="h-4 w-4" />;
      case "Events":
        return <Calendar className="h-4 w-4" />;
      case "Blog & Articles":
        return <User className="h-4 w-4" />;
      case "Photo Galleries":
        return <Camera className="h-4 w-4" />;
      case "Video Galleries":
        return <Play className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (type: string) => {
    switch (type) {
      case "Latest News":
        return "bg-trust-red/10 text-trust-red border-trust-red/20";
      case "Events":
        return "bg-trust-blue/10 text-trust-blue border-trust-blue/20";
      case "Blog & Articles":
        return "bg-success-green/10 text-success-green border-success-green/20";
      case "Photo Galleries":
        return "bg-accent/10 text-accent-foreground border-accent/20";
      case "Video Galleries":
        return "bg-warning-orange/10 text-warning-orange border-warning-orange/20";
      default:
        return "bg-muted/10 text-muted-foreground border-muted/20";
    }
  };

  const openModal = (article: any) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    if (modalRef.current && modalContentRef.current) {
      const tl = gsap.timeline({
        onComplete: () => {
          setIsModalOpen(false);
          setSelectedArticle(null);
          document.body.style.overflow = 'unset';
        }
      });
      
      tl.to(modalContentRef.current, {
        scale: 0.8,
        opacity: 0,
        duration: 0.3,
        ease: "power2.in"
      })
      .to(modalRef.current, {
        opacity: 0,
        duration: 0.2,
        ease: "power2.in"
      }, "-=0.1");
    }
  };

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animations
      if (headerRef.current) {
        const headerTitle = headerRef.current.querySelector("h1");
        const headerSubtitle = headerRef.current.querySelector("h2");
        const headerDescription = headerRef.current.querySelector("p");

        if (headerTitle) {
          gsap.from(headerTitle, {
            y: 50,
            opacity: 0,
            duration: 1,
            ease: "power3.out"
          });
        }

        if (headerSubtitle) {
          gsap.from(headerSubtitle, {
            y: 30,
            opacity: 0,
            duration: 1,
            delay: 0.2,
            ease: "power3.out"
          });
        }

        if (headerDescription) {
          gsap.from(headerDescription, {
            y: 20,
            opacity: 0,
            duration: 1,
            delay: 0.4,
            ease: "power3.out"
          });
        }
      }

      // Featured section animations
      if (featuredRef.current) {
        const featuredCards = featuredRef.current.querySelectorAll(".featured-card");
        
        gsap.from(featuredCards, {
          y: 80,
          opacity: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: featuredRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        });
      }

      // Grid animations
      if (gridRef.current) {
        const gridCards = gridRef.current.querySelectorAll(".grid-card");
        
        gsap.set(gridCards, { opacity: 1, scale: 1 }); // Ensure cards are visible by default
        
        gsap.from(gridCards, {
          y: 30,
          opacity: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 90%",
            end: "bottom 10%",
            toggleActions: "play none none none"
          }
        });
      }
    });

    return () => ctx.revert();
  }, [filteredItems]);

  // Modal animations
  useEffect(() => {
    if (isModalOpen && modalRef.current && modalContentRef.current) {
      gsap.set(modalRef.current, { opacity: 0 });
      gsap.set(modalContentRef.current, { scale: 0.8, opacity: 0 });

      const tl = gsap.timeline();
      tl.to(modalRef.current, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out"
      })
      .to(modalContentRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.4,
        ease: "power3.out"
      }, "-=0.1");

      // Animate content paragraphs
      const paragraphs = modalContentRef.current.querySelectorAll(".content-paragraph");
      gsap.from(paragraphs, {
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        delay: 0.5,
        ease: "power2.out"
      });
    }
  }, [isModalOpen]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        {/* Page Header */}
        <div ref={headerRef} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            News & Media Hub
          </h1>
          <h2 className="text-2xl md:text-3xl text-muted-foreground mb-6 font-light">
            Stay Informed, Inspired, and Connected
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Welcome to the Natcon Sacco News & Media Hub. This is your central resource for the latest official announcements, 
            upcoming events, insightful financial articles, and inspiring stories from our community.
          </p>
        </div>

        {/* Featured Section */}
        <section ref={featuredRef} className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary/30 rounded-full"></div>
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">Featured Stories</h3>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredItems.map((item, index) => (
              <Card 
                key={item.id} 
                className={`featured-card group overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 cursor-pointer ${
                  index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                }`}
                onClick={() => openModal(item)}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className={`w-full object-cover group-hover:scale-110 transition-transform duration-700 ${
                      index === 0 ? 'h-80 lg:h-96' : 'h-48'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-4 left-4">
                    <Badge className={`${getCategoryColor(item.type)} backdrop-blur-sm`}>
                      {getCategoryIcon(item.type)}
                      <span className="ml-1">{item.type}</span>
                    </Badge>
                  </div>
                  {item.readTime && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-black/30 text-white backdrop-blur-sm border-white/20">
                        <Clock className="h-3 w-3 mr-1" />
                        {item.readTime}
                      </Badge>
                    </div>
                  )}
                </div>
                
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{item.date}</span>
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <CardTitle className={`leading-tight group-hover:text-primary transition-colors ${
                    index === 0 ? 'text-xl lg:text-2xl' : 'text-lg'
                  }`}>
                    {item.title}
                  </CardTitle>
                  <CardDescription className="leading-relaxed">
                    {item.excerpt}
                  </CardDescription>
                  {item.author && (
                    <p className="text-sm text-muted-foreground font-medium">{item.author}</p>
                  )}
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        {/* Filter Bar */}
        <div className="mb-12">
          <div className="flex flex-wrap gap-3 justify-center">
            {filters.map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? "default" : "outline"}
                onClick={() => setActiveFilter(filter)}
                className="rounded-full px-6 py-2 transition-all duration-300 hover:scale-105"
              >
                {filter}
              </Button>
            ))}
          </div>
        </div>

        {/* Content Grid - All Articles */}
        <section ref={gridRef}>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary/30 rounded-full"></div>
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">All Articles</h3>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <Card 
                key={item.id} 
                className="grid-card group overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5 cursor-pointer hover:-translate-y-2"
                onClick={() => openModal(item)}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-4 left-4">
                    <Badge className={`${getCategoryColor(item.type)} backdrop-blur-sm`}>
                      {getCategoryIcon(item.type)}
                      <span className="ml-1">{item.type}</span>
                    </Badge>
                  </div>
                  {item.readTime && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-black/30 text-white backdrop-blur-sm border-white/20">
                        <Clock className="h-3 w-3 mr-1" />
                        {item.readTime}
                      </Badge>
                    </div>
                  )}
                </div>
                
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{item.date}</span>
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <CardTitle className="text-lg leading-tight group-hover:text-primary transition-colors">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="leading-relaxed">
                    {item.excerpt}
                  </CardDescription>
                  {item.author && (
                    <p className="text-sm text-muted-foreground font-medium">{item.author}</p>
                  )}
                  {item.location && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      📍 {item.location}
                    </p>
                  )}
                  {item.time && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      ⏰ {item.time}
                    </p>
                  )}
                  {item.theme && (
                    <p className="text-sm font-semibold text-primary mt-2">
                      🎯 Theme: {item.theme}
                    </p>
                  )}
                  {item.dressCode && (
                    <p className="text-sm text-muted-foreground">
                      👔 Dress Code: {item.dressCode}
                    </p>
                  )}
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        {/* Video Gallery Section */}
        {(activeFilter === "All" || activeFilter === "Video Galleries") && videoItems.length > 0 && (
          <section className="mt-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-1 w-12 bg-gradient-to-r from-warning-orange to-warning-orange/30 rounded-full"></div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground">Video Gallery</h3>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videoItems.map((video) => (
                <Card 
                  key={video.id}
                  className="group overflow-hidden border-border/50 hover:border-warning-orange/30 transition-all duration-500 hover:shadow-xl hover:shadow-warning-orange/10 cursor-pointer hover:-translate-y-2"
                  onClick={() => openVideoModal(video)}
                >
                  {/* Video Thumbnail with Play Button Overlay */}
                  <div className="relative aspect-video bg-gradient-to-br from-muted to-muted/50 overflow-hidden">
                    {/* Video Preview - First Frame */}
                    <video 
                      src={video.videoUrl}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      muted
                      preload="metadata"
                    />
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-warning-orange/90 group-hover:bg-warning-orange group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg shadow-warning-orange/30">
                        <Play className="h-8 w-8 md:h-10 md:w-10 text-white fill-white ml-1" />
                      </div>
                    </div>
                    
                    {/* Duration Badge */}
                    <div className="absolute bottom-4 right-4">
                      <Badge className="bg-black/60 text-white backdrop-blur-sm border-white/20">
                        <Play className="h-3 w-3 mr-1" />
                        {video.duration}
                      </Badge>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-warning-orange/10 text-warning-orange border-warning-orange/20 backdrop-blur-sm">
                        <Play className="h-4 w-4 mr-1" />
                        Video
                      </Badge>
                    </div>
                  </div>
                  
                  <CardHeader className="space-y-3">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{video.date}</span>
                      <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <CardTitle className="text-lg leading-tight group-hover:text-warning-orange transition-colors">
                      {video.title}
                    </CardTitle>
                    <CardDescription className="leading-relaxed">
                      {video.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* View All News Button */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-primary/30"></div>
            <span className="text-muted-foreground font-medium">Explore More</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-primary/30"></div>
          </div>
          <Button 
            size="lg" 
            className="group px-8 py-4 rounded-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            View All News & Updates
            <ChevronRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            Stay updated with the latest news, insights, and updates from Natcon Sacco
          </p>
        </div>

        {/* Modern Modal */}
        {isModalOpen && selectedArticle && (
          <div 
            ref={modalRef}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={(e) => e.target === modalRef.current && closeModal()}
          >
            <div 
              ref={modalContentRef}
              className="relative w-full max-w-4xl max-h-[90vh] bg-background rounded-3xl shadow-2xl overflow-hidden"
            >
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border/50 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={getCategoryColor(selectedArticle.type)}>
                      {getCategoryIcon(selectedArticle.type)}
                      <span className="ml-1">{selectedArticle.category}</span>
                    </Badge>
                    <span className="text-sm text-muted-foreground">{selectedArticle.date}</span>
                    {selectedArticle.readTime && (
                      <Badge variant="outline" className="ml-auto">
                        <Clock className="h-3 w-3 mr-1" />
                        {selectedArticle.readTime}
                      </Badge>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={closeModal}
                    className="rounded-full h-10 w-10 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="relative">
                  <img 
                    src={selectedArticle.image} 
                    alt={selectedArticle.title} 
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                
                <div className="p-8 space-y-6">
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                    {selectedArticle.title}
                  </h1>
                  
                  {selectedArticle.author && (
                    <p className="text-muted-foreground font-medium">{selectedArticle.author}</p>
                  )}
                  
                  {(selectedArticle.location || selectedArticle.time || selectedArticle.theme || selectedArticle.dressCode) && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 space-y-3">
                      <h3 className="text-lg font-bold text-primary mb-4">Event Details</h3>
                      {selectedArticle.location && (
                        <p className="text-foreground flex items-center gap-2">
                          <span className="text-xl">📍</span>
                          <span><strong>Location:</strong> {selectedArticle.location}</span>
                        </p>
                      )}
                      {selectedArticle.time && (
                        <p className="text-foreground flex items-center gap-2">
                          <span className="text-xl">⏰</span>
                          <span><strong>Time:</strong> {selectedArticle.time}</span>
                        </p>
                      )}
                      {selectedArticle.theme && (
                        <p className="text-foreground flex items-center gap-2">
                          <span className="text-xl">🎯</span>
                          <span><strong>Theme:</strong> {selectedArticle.theme}</span>
                        </p>
                      )}
                      {selectedArticle.dressCode && (
                        <p className="text-foreground flex items-center gap-2">
                          <span className="text-xl">👔</span>
                          <span><strong>Dress Code:</strong> {selectedArticle.dressCode}</span>
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="prose prose-slate dark:prose-invert max-w-none">
                    {selectedArticle.content.split('\n\n').map((paragraph: string, index: number) => {
                      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        // Handle bold headings
                        const heading = paragraph.slice(2, -2);
                        return (
                          <h3 key={index} className="content-paragraph text-xl font-bold text-foreground mt-8 mb-4 first:mt-0">
                            {heading}
                          </h3>
                        );
                      } else if (paragraph.startsWith('- ')) {
                        // Handle bullet points
                        const items = paragraph.split('\n- ').map(item => item.replace(/^- /, ''));
                        return (
                          <ul key={index} className="content-paragraph list-disc list-inside space-y-2 my-4 ml-4">
                            {items.map((item, itemIndex) => (
                              <li key={itemIndex} className="text-foreground/90 leading-relaxed">
                                {item}
                              </li>
                            ))}
                          </ul>
                        );
                      } else {
                        // Handle regular paragraphs
                        return (
                          <p key={index} className="content-paragraph text-base leading-relaxed mb-6 text-foreground/90">
                            {paragraph}
                          </p>
                        );
                      }
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Player Modal */}
        {isVideoModalOpen && selectedVideo && (
          <div 
            ref={videoModalRef}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={(e) => e.target === videoModalRef.current && closeVideoModal()}
          >
            <div className="relative w-full max-w-5xl">
              {/* Close Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={closeVideoModal}
                className="absolute -top-12 right-0 rounded-full h-10 w-10 p-0 bg-white/10 hover:bg-white/20 text-white z-10"
              >
                <X className="h-5 w-5" />
              </Button>
              
              {/* Video Title */}
              <div className="absolute -top-12 left-0 text-white">
                <h3 className="text-lg font-semibold">{selectedVideo.title}</h3>
              </div>
              
              {/* Video Player */}
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
                <video 
                  ref={videoRef}
                  src={selectedVideo.videoUrl}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Video Description */}
              <div className="mt-4 text-white/80 text-center">
                <p>{selectedVideo.description}</p>
                <p className="text-white/50 text-sm mt-2">{selectedVideo.date}</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default NewsMedia;